import { Box, Divider, Typography } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import StopIcon from "../../../../assets/images/icons/StopIcon.tsx";
import StoppableButton from "../../../../components/common/StoppableButton/StoppableButton.tsx";
import { APPS } from "../../../../constants/AppsConstants.ts";
import { MAX_ALLOWED_SELECTED_IMAGES } from "../../../../constants/ImageToImageConstants.ts";
import LinkConstants from "../../../../constants/LinkConstants.ts";
import { useImageToImageConfig } from "../../../../hooks/useImageToImageConfig.tsx";
import { useLogEvents } from "../../../../hooks/useLogEvents.ts";
import { useManualPlacementConfig } from "../../../../hooks/useManualPlacementConfig.ts";
import { useAppStore } from "../../../../hooks/useStores.tsx";
import ExpandableMenuButton from "../../../../layout/ExpandableMenu/Button/ExpandableMenuButton.tsx";
import ExpandableMenuLayout from "../../../../layout/ExpandableMenu/Menu/ExpandableMenuLayout.tsx";
import { ImageEditingViewMode, ImageToImageConfigType } from "../../../../models/image-to-image.ts";
import { isPlaygroundLoadingImages } from "../../../../utils/index.ts";
import iframeStore from "../../../IframeNew/iframe-store.tsx";
import ConfigButtonLabel from "../../ImageToImage/Config/ConfigButtonLabel/ConfigButtonLabel.tsx";
import ConfigDisplay from "../../ImageToImage/Config/ConfigDisplay/ConfigDisplay";
import BackgroundConfig from "../../ImageToImage/Config/Features/Background/BackgroundConfig";
import EraseObject from "../../ImageToImage/Config/Features/EraseObject/EraseObject.tsx";
import ForegroundPlacement from "../../ImageToImage/Config/Features/ForegroundPlacement/ForegroundPlacement";
import ManualPlacement from "../../ImageToImage/Config/Features/ManualPlacement/ManualPlacement.tsx";
import SizeConfig from "../../ImageToImage/Config/Features/Size/SizeConfig";
import ImageToImageError from "../../ImageToImage/Error/ImageToImageError.tsx";
import styles from "./AutomotiveConfig.module.scss";
import AutomotiveEffects from "./Effects/AutomotiveEffects.tsx";
import AutomotiveHarmonization from "./Harmonization/AutomotiveHarmonization.tsx";
import AutomotiveReflection from "./Reflection/AutomotiveReflection.tsx";

const AutomotiveConfig = () => {
	const { t } = useTranslation("translation", { keyPrefix: "playground.automotive.config" });
	const { imageToImageStore, playgroundStore, tourStore } = useAppStore();
	const { logAutomotiveEvents } = useLogEvents();
	const { automotiveStore } = playgroundStore;
	const {
		isGenerateEnabled,
		updateSelectedBackgroundConfigs,
		updateSelectedSizeConfigs,
		updateSelectedForegroundPlacementConfigs,
	} = useImageToImageConfig();
	const { checkResolutions } = useManualPlacementConfig();

	const [activeConfig, setActiveConfig] = useState<string>("");
	const [isSubMenuOpen, setIsSubMenuOpen] = useState<boolean>(false);
	const [isSingleMode, setIsSingleMode] = useState<boolean>(false);

	const hasLoadingImages = isPlaygroundLoadingImages(playgroundStore.playgroundResults, APPS.AUTOMOTIVE);

	useEffect(() => {
		if (playgroundStore.selectedConfig !== APPS.AUTOMOTIVE) {
			playgroundStore.handleAppChange(APPS.AUTOMOTIVE);
		}
		setIsSingleMode(playgroundStore.imageEditingViewMode === ImageEditingViewMode.SINGLE);
	}, [imageToImageStore.isGeneratingImages, playgroundStore.imageEditingViewMode]);

	const handleGenerateImageToImage = async () => {
		await imageToImageStore.generateImageToImage();
		checkResolutions();
		logAutomotiveEvents();
	};

	const handleDeleteConfig = (configKey: keyof ImageToImageConfigType, field: string, value: any) => {
		const currentConfig = { ...(imageToImageStore.config[configKey] as Record<string, any> | undefined) };

		imageToImageStore.handleConfigChange(configKey, {
			...currentConfig,
			[field]: value,
		});
	};

	const handleAbortExpand = (config?: string) => {
		automotiveStore.configToSwitch = config;
		automotiveStore.openWarningPopup = true;
	};

	return (
		<Box className={styles.wrapper}>
			<ExpandableMenuLayout
				className={clsx(styles.config, styles.imageToImageConfigs)}
				abortExpand={!automotiveStore.isSaved}
				onAbortExpand={handleAbortExpand}
				setActiveConfig={setActiveConfig}
				isSubMenuOpen={isSubMenuOpen}
				hideSubPanel={tourStore.activeTour?.name === "feedback-reminder"}
			>
				<Box className={styles.configHeader}>
					<Typography variant="h6" className={styles.title}>
						{isSingleMode ? t("singleModeTitle") : t("bulkModeTitle")}
					</Typography>
					<Typography className={styles.subTitle}>
						{isSingleMode ? t("singleModeSubTitle") : t("bulkModeSubTitle")}
					</Typography>
				</Box>

				{(!iframeStore.isIframe() || iframeStore.iframe?.config.automotive_config?.image_size) && (
					<ExpandableMenuButton
						expandTo={<SizeConfig />}
						subMenuPanelProps={{ title: t("features.size.title"), subTitle: t("features.size.subTitle") }}
						isActive={activeConfig === t("features.size.title")}
						hidden={isSingleMode}
					>
						<ConfigButtonLabel
							className={clsx(styles.buttonLabel)}
							label={t("features.size.button")}
							info={t("features.size.tooltip")}
						/>
					</ExpandableMenuButton>
				)}
				{(!iframeStore.isIframe() || iframeStore.iframe?.config.automotive_config?.image_size) &&
					(isSingleMode ? (
						<ExpandableMenuButton
							expandTo={<ManualPlacement />}
							subMenuPanelProps={{
								title: t("features.manualPlacement.title"),
								subTitle: t("features.manualPlacement.subTitle"),
							}}
							isActive={activeConfig === t("features.manualPlacement.title")}
						>
							<ConfigButtonLabel
								className={clsx(styles.buttonLabel)}
								label={t("features.manualPlacement.button")}
								info={t("features.manualPlacement.tooltip-automotive")}
							/>
						</ExpandableMenuButton>
					) : (
						<ConfigDisplay
							config={imageToImageStore.config.size}
							onDelete={(configKey, value) => {
								handleDeleteConfig("size", configKey, value);
								updateSelectedSizeConfigs();
							}}
							expandTo={<SizeConfig />}
							subMenuPanelProps={{ title: t("features.size.title") }}
						/>
					))}

				{(!iframeStore.isIframe() || iframeStore.iframe?.config.automotive_config?.image_size) && (
					<Divider className={styles.divider} />
				)}

				{(!iframeStore.isIframe() || iframeStore.iframe?.config.automotive_config?.scenery) && (
					<ExpandableMenuButton
						expandTo={<BackgroundConfig onSubMenuOpen={setIsSubMenuOpen} hiddenConfigs={["blur"]} />}
						subMenuPanelProps={{
							title: t("features.background.title"),
							subTitle: t("features.background.subTitle"),
						}}
						isActive={activeConfig === t("features.background.title")}
					>
						<ConfigButtonLabel
							className={clsx(styles.buttonLabel)}
							label={t("features.background.button")}
							info={t("features.background.tooltip")}
						/>
					</ExpandableMenuButton>
				)}

				{!isSingleMode && (
					<ConfigDisplay
						config={imageToImageStore.config.background}
						onDelete={(configKey, value) => {
							handleDeleteConfig("background", configKey, value);
							updateSelectedBackgroundConfigs();
						}}
						expandTo={<BackgroundConfig onSubMenuOpen={setIsSubMenuOpen} />}
						subMenuPanelProps={{ title: t("features.background.title") }}
					/>
				)}

				{(!iframeStore.isIframe() || iframeStore.iframe?.config.automotive_config?.scenery) && (
					<Divider className={styles.divider} />
				)}

				{!isSingleMode && (
					<ConfigDisplay
						config={imageToImageStore.config.foreground_placement}
						onDelete={(configKey, value) => {
							handleDeleteConfig("foreground_placement", configKey, value);
							updateSelectedForegroundPlacementConfigs();
						}}
						expandTo={<ForegroundPlacement />}
						subMenuPanelProps={{ title: t("features.foregroundPlacement.title") }}
					/>
				)}
				{/* {!isSingleMode && <Divider className={styles.divider} />} */}

				{(!iframeStore.isIframe() || iframeStore.iframe?.config.automotive_config?.reflections) && (
					<ExpandableMenuButton
						expandTo={<AutomotiveReflection {...{ setActiveConfig, isTires: true }} />}
						subMenuPanelProps={{
							title: t("features.tiresRefinement.title"),
							subTitle: t("features.tiresRefinement.subTitle"),
							apiLink: LinkConstants.PRODUCT_SHOT.AUTOMOTIVE.WHEELS.DOCS,
						}}
						isActive={activeConfig === t("features.tiresRefinement.title")}
						hidden={!isSingleMode}
					>
						<ConfigButtonLabel
							className={clsx(styles.buttonLabel)}
							label={t("features.tiresRefinement.button")}
							info={t("features.tiresRefinement.tooltip")}
							isPrimary
						/>
					</ExpandableMenuButton>
				)}
				{(!iframeStore.isIframe() || iframeStore.iframe?.config.automotive_config?.reflections) &&
					isSingleMode && <Divider className={styles.divider} />}

				{(!iframeStore.isIframe() || iframeStore.iframe?.config.automotive_config?.reflections) && (
					<ExpandableMenuButton
						expandTo={<AutomotiveReflection {...{ setActiveConfig }} />}
						subMenuPanelProps={{
							title: t("features.reflection.title"),
							subTitle: t("features.reflection.subTitle"),
							apiLink: LinkConstants.BRIA_API_REFLECTIONS,
						}}
						isActive={activeConfig === t("features.reflection.title")}
						hidden={!isSingleMode}
					>
						<ConfigButtonLabel
							className={clsx(styles.buttonLabel)}
							label={t("features.reflection.button")}
							info={t("features.reflection.tooltip")}
							isPrimary
						/>
					</ExpandableMenuButton>
				)}

				{(!iframeStore.isIframe() || iframeStore.iframe?.config.automotive_config?.reflections) &&
					isSingleMode && <Divider className={styles.divider} />}

				{(!iframeStore.isIframe() || iframeStore.iframe?.config.automotive_config?.effects) && (
					<ExpandableMenuButton
						expandTo={<AutomotiveEffects {...{ setActiveConfig }} />}
						subMenuPanelProps={{
							title: t("features.effects.title"),
							subTitle: t("features.effects.subTitle"),
							apiLink: LinkConstants.BRIA_API_EFFECTS,
						}}
						isActive={activeConfig === t("features.effects.title")}
						hidden={!isSingleMode}
					>
						<ConfigButtonLabel
							className={clsx(styles.buttonLabel)}
							label={t("features.effects.button")}
							info={t("features.effects.tooltip")}
							isPrimary
						/>
					</ExpandableMenuButton>
				)}
				{(!iframeStore.isIframe() || iframeStore.iframe?.config.automotive_config?.effects) && isSingleMode && (
					<Divider className={styles.divider} />
				)}

				{(!iframeStore.isIframe() || iframeStore.iframe?.config.automotive_config?.harmonization) && (
					<ExpandableMenuButton
						expandTo={<AutomotiveHarmonization {...{ setActiveConfig }} />}
						subMenuPanelProps={{
							title: t("features.harmonization.title"),
							subTitle: t("features.harmonization.subTitle"),
							apiLink: LinkConstants.BRIA_API_HARMONIZATION,
						}}
						isActive={activeConfig === t("features.harmonization.title")}
						hidden={!isSingleMode}
					>
						<ConfigButtonLabel
							className={clsx(styles.buttonLabel)}
							label={t("features.harmonization.button")}
							info={t("features.harmonization.tooltip")}
							isPrimary
						/>
					</ExpandableMenuButton>
				)}
				{(!iframeStore.isIframe() || iframeStore.iframe?.config.automotive_config?.harmonization) &&
					isSingleMode && <Divider className={styles.divider} />}

				{(!iframeStore.isIframe() || iframeStore.iframe?.config.automotive_config?.modify_objects) && (
					<ExpandableMenuButton
						expandTo={<EraseObject />}
						subMenuPanelProps={{
							title: t("features.eraseObject.title-automotive"),
							subTitle: t("features.eraseObject.subTitle"),
						}}
						isActive={activeConfig === t("features.eraseObject.title")}
						hidden={!isSingleMode}
					>
						<ConfigButtonLabel
							className={clsx(styles.buttonLabel)}
							label={t("features.eraseObject.button-automotive")}
							info={t("features.eraseObject.tooltip-automotive")}
						/>
					</ExpandableMenuButton>
				)}
				<Box className={styles.imageToImageErrorContainer}>
					<ImageToImageError />
				</Box>
			</ExpandableMenuLayout>

			{!isSingleMode && (
				<StoppableButton
					condition={
						hasLoadingImages &&
						(!imageToImageStore.editingCanvasStore.isEraseConfigsEnabled ||
							!imageToImageStore.isGeneratingImages)
					}
					onStopButtonClick={() => imageToImageStore.abortImageGeneration()}
					onButtonClick={handleGenerateImageToImage}
					disabled={!isGenerateEnabled()}
					buttonLabel={
						<>
							{imageToImageStore.getGeneratedImagesCount > MAX_ALLOWED_SELECTED_IMAGES
								? t("tooManySelected")
								: t("button")}
							{imageToImageStore.getGeneratedImagesCount > 0 &&
								isGenerateEnabled() &&
								` ${imageToImageStore.getGeneratedImagesCount} ${t("results")}`}
						</>
					}
					stopButtonLabel={
						<>
							<StopIcon /> {t("stopGenerating")}
						</>
					}
					fullWidth
					className={`generate-image-to-image ${styles.stopGeneratingBt}`}
				/>
			)}
		</Box>
	);
};

export default observer(AutomotiveConfig);
